import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { band } from '@/constants/portfolio'
import { audioEngine } from '@/lib/audio/engine'
import { scrollToSection } from '@/lib/scroll-to'
import { useExperience } from '@/components/generals/experience-provider'

type MenuItem = {
    key: string
    label: string
    hint: string
    href: string
    kind: 'section' | 'external' | 'download'
}

const buildItems = (): MenuItem[] => {
    const items: MenuItem[] = [
        { key: 'about', label: 'About', hint: 'Who they are', href: '#about', kind: 'section' },
        { key: 'members', label: 'Members', hint: 'Choose your player', href: '#members', kind: 'section' },
        { key: 'music', label: 'Music', hint: 'Three songs, three stories', href: '#music', kind: 'section' },
        { key: 'journey', label: 'Journey', hint: 'Live on stage', href: '#journey', kind: 'section' },
        { key: 'vision', label: 'Vision', hint: 'Malaysia to the world', href: '#vision', kind: 'section' },
        { key: 'book', label: 'Book us', hint: 'Instagram DM', href: band.socialLinks.instagram, kind: 'external' }
    ]

    if (band.socialLinks.pressKitUrl) {
        items.push({ key: 'press', label: 'Press kit', hint: 'PDF download', href: band.socialLinks.pressKitUrl, kind: 'download' })
    }

    return items
}

const ITEMS = buildItems()

const Toggle: FC<{ label: string, on: boolean, onChange: (value: boolean) => void }> = ({ label, on, onChange }): ReactNode => (
    <button
        type='button'
        aria-pressed={on}
        onClick={() => onChange(!on)}
        className='group flex min-h-11 items-center gap-3 text-left'
    >
        <span className='mono-label text-[11px] text-paper/70'>{label}</span>
        <span
            aria-hidden='true'
            className={cn(
                'relative flex h-7 w-[86px] -skew-x-12 items-center border-2 border-paper/60 transition-colors',
                on ? 'bg-paper' : 'bg-transparent'
            )}
        >
            <span className={cn('mono-label w-1/2 skew-x-12 text-center text-[10px]', on ? 'text-ink' : 'text-paper/40')}>On</span>
            <span className={cn('mono-label w-1/2 skew-x-12 text-center text-[10px]', on ? 'text-ink/40' : 'text-paper')}>Off</span>
        </span>
    </button>
)

// Persona-style main menu: huge slanted options, a red slash as the cursor,
// arrow keys, number keys, Enter and Esc. Focus stays inside while it is open.
const MenuOverlay: FC = (): ReactNode => {
    const { menuOpen, setMenuOpen, sfx, setSfx, threeD, setThreeD, reducedMotion } = useExperience()
    const [mounted, setMounted] = useState(false)
    const [closing, setClosing] = useState(false)
    const [selected, setSelected] = useState(0)
    const dialogRef = useRef<HTMLDivElement>(null)
    const itemRefs = useRef<Array<HTMLAnchorElement | null>>([])
    const returnFocus = useRef<HTMLElement | null>(null)

    useEffect(() => {
        if (menuOpen) {
            returnFocus.current = document.activeElement as HTMLElement | null
            setMounted(true)
            setClosing(false)
            return
        }

        if (!mounted) return

        setClosing(true)
        const timer = window.setTimeout(() => {
            setMounted(false)
            setClosing(false)
            returnFocus.current?.focus?.()
        }, reducedMotion ? 10 : 320)

        return () => window.clearTimeout(timer)
    }, [menuOpen, mounted, reducedMotion])

    useEffect(() => {
        if (!mounted || closing) return

        const previous = document.documentElement.style.overflow
        document.documentElement.style.overflow = 'hidden'
        const timer = window.setTimeout(() => itemRefs.current[selected]?.focus(), 30)

        return () => {
            window.clearTimeout(timer)
            document.documentElement.style.overflow = previous
        }
        // Focus only when the menu opens, not on every selection change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mounted, closing])

    const close = useCallback(() => {
        audioEngine.cue('back')
        setMenuOpen(false)
    }, [setMenuOpen])

    const select = (index: number, focus = true) => {
        const next = (index + ITEMS.length) % ITEMS.length
        if (next !== selected) audioEngine.cue('move')
        setSelected(next)
        if (focus) itemRefs.current[next]?.focus()
    }

    const activate = (item: MenuItem) => {
        audioEngine.cue('confirm')

        if (item.kind === 'section') {
            setMenuOpen(false)
            window.setTimeout(() => scrollToSection(item.key), reducedMotion ? 0 : 200)
        }
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault()
            close()
            return
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault()
            select(selected + 1)
            return
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault()
            select(selected - 1)
            return
        }

        if (event.key === 'Home') {
            event.preventDefault()
            select(0)
            return
        }

        if (event.key === 'End') {
            event.preventDefault()
            select(ITEMS.length - 1)
            return
        }

        const number = Number(event.key)
        if (Number.isInteger(number) && number >= 1 && number <= ITEMS.length) {
            event.preventDefault()
            select(number - 1)
            return
        }

        if (event.key === 'Tab') {
            const focusable = Array.from(
                dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? []
            )
            if (focusable.length === 0) return

            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }
    }

    if (!mounted) return null

    const current = ITEMS[selected]

    return (
        <div
            ref={dialogRef}
            id='site-menu'
            role='dialog'
            aria-modal='true'
            aria-label='Menu'
            onKeyDown={onKeyDown}
            className={cn('menu-overlay fixed inset-0 z-[100] overflow-y-auto bg-ink text-paper', closing && 'menu-overlay-closing')}
        >
            <div aria-hidden='true' className='pointer-events-none absolute inset-y-0 right-[-12vw] hidden w-[42vw] -skew-x-12 bg-paper tab:block'>
                <span className='display-type absolute bottom-[8vh] left-[10vw] block skew-x-12 text-[34vh] leading-[0.75] text-ink/90'>
                    {String(selected + 1).padStart(2, '0')}
                </span>
            </div>

            <div className='relative flex min-h-full flex-col px-5 pb-8 pt-5 xs:px-8 tab:px-12 tab:pb-10 tab:pt-6'>
                <div className='flex items-center justify-between gap-4'>
                    <p className='display-type text-[20px] tracking-[-0.05em] tab:text-[22px]'>Bluntsheep</p>

                    <button
                        type='button'
                        onClick={close}
                        className='group relative flex min-h-11 items-center gap-3 overflow-hidden border-2 border-paper px-4 tab:border-ink tab:text-ink'
                    >
                        <span className='mono-label text-xs'>Close</span>
                        <span className='mono-label border border-current px-1.5 py-0.5 text-[10px]'>Esc</span>
                    </button>
                </div>

                <nav aria-label='Menu' className='mt-8 flex-1 tab:mt-4 tab:flex tab:items-center'>
                    <ul className='flex flex-col gap-1 tab:gap-0'>
                        {ITEMS.map((item, index) => {
                            const isSelected = index === selected
                            const tilt = (index - (ITEMS.length - 1) / 2) * 1.6

                            return (
                                <li
                                    key={item.key}
                                    style={{ transform: `translateX(${index * 1.4}vw) rotate(${tilt}deg)` }}
                                >
                                    <div className='menu-item' style={{ animationDelay: `${80 + index * 55}ms` }}>
                                    <a
                                        ref={element => {
                                            itemRefs.current[index] = element
                                        }}
                                        href={item.href}
                                        tabIndex={isSelected ? 0 : -1}
                                        target={item.kind === 'section' ? undefined : '_blank'}
                                        rel={item.kind === 'section' ? undefined : 'noopener noreferrer'}
                                        download={item.kind === 'download' ? '' : undefined}
                                        onMouseEnter={() => select(index, false)}
                                        onFocus={() => setSelected(index)}
                                        onClick={event => {
                                            if (item.kind === 'section') event.preventDefault()
                                            activate(item)
                                        }}
                                        className='group relative flex min-h-11 items-center gap-4 py-1 outline-offset-8 tab:gap-6'
                                    >
                                        <span className='mono-label w-7 shrink-0 text-xs text-paper/50'>{String(index + 1).padStart(2, '0')}</span>

                                        <span className='relative block'>
                                            <span
                                                aria-hidden='true'
                                                className={cn(
                                                    'menu-cursor absolute -inset-x-4 inset-y-[12%] origin-left bg-bandana transition-transform duration-200 ease-out',
                                                    isSelected ? 'scale-x-100' : 'scale-x-0'
                                                )}
                                            />
                                            <span
                                                className={cn(
                                                    'display-type menu-skew relative block text-[clamp(2.4rem,8.4vw,6.6rem)] transition-colors duration-150',
                                                    isSelected ? 'menu-label-selected text-paper' : 'text-paper/35 group-hover:text-paper/70'
                                                )}
                                            >
                                                {item.label}
                                            </span>
                                        </span>

                                        {item.kind !== 'section' && (
                                            <span className='sr-only'>(opens in a new tab)</span>
                                        )}
                                    </a>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='mt-10 flex flex-col gap-8 tab:mt-6 tab:flex-row tab:items-end tab:justify-between'>
                    <div className='flex flex-col gap-4'>
                        <p className='mono-label text-[11px] text-paper/70' aria-live='polite'>
                            <span className='text-paper'>{current.label}</span>
                            <span className='px-2 text-paper/40'>/</span>
                            {current.hint}
                        </p>

                        <div className='flex flex-wrap items-center gap-x-8 gap-y-2'>
                            <Toggle label='Sound effects' on={sfx} onChange={value => setSfx(value)} />
                            <Toggle label='3D and motion' on={threeD} onChange={value => setThreeD(value)} />
                        </div>
                    </div>

                    <ul className='hidden flex-wrap items-center gap-5 wide:flex wide:text-ink' aria-label='Keyboard controls'>
                        {[
                            ['↑ ↓', 'Select'],
                            ['Enter', 'Confirm'],
                            ['Esc', 'Close']
                        ].map(([key, action]) => (
                            <li key={action} className='flex items-center gap-2'>
                                <span className='mono-label border-2 border-current px-2 py-1 text-[10px]'>{key}</span>
                                <span className='text-sm font-bold italic'>{action}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    )
}

export default MenuOverlay
